import { makeVar } from "@apollo/client";
import { ReactNode } from "react";
import {
    CartsDocument,
    InventoryDocument,
    InventoryItem,
    Scan,
    ScansDocument,
    useKeepAliveSubscription,
    useOnCartsUpdatedSubscription,
    useOnInventoryItemAddedSubscription,
    useOnInventoryItemDeletedSubscription,
    useOnInventoryItemUpdatedSubscription,
    useOnOrderErrorSubscription,
    useOnScansUpdatedSubscription,
} from "../../Generated/graphql";

export const orderServiceErrorVar = makeVar({
  firstErrorTime: "",
  currentlyActive: false,
});

export const Subscribers = ({
  accountId,
  children,
}: {
  accountId: string;
  children: ReactNode;
}) => {
  useKeepAliveSubscription();

  // On each subscription callback we update the reactive var
  useOnOrderErrorSubscription({
    onSubscriptionData: ({ subscriptionData }) => {
      const data = subscriptionData.data?.onOrderError;
      if (data) {
        orderServiceErrorVar({
          firstErrorTime: data.currentlyActive ? data.firstErrorTime : "",
          currentlyActive: data.currentlyActive,
        });
      }
    },
  });

  useOnCartsUpdatedSubscription({
    variables: {
      accountId,
    },
    onSubscriptionData: ({ client, subscriptionData }) => {
      const cache = client.cache;

      if (subscriptionData.data && subscriptionData.data?.onCartsUpdated) {
        cache.writeQuery({
          query: CartsDocument,
          data: {
            carts: subscriptionData.data?.onCartsUpdated,
          },
        });
      }
    },
  });

  useOnInventoryItemUpdatedSubscription({
    variables: {
      accountId,
    },
    onSubscriptionData: ({ client, subscriptionData }) => {
      const cache = client.cache;
      const previous = cache.readQuery<{ inventory: InventoryItem[] }>({
        query: InventoryDocument,
      });

      if (!previous) {
        return;
      }

      // Map the inventory, and merge the updated fields with existing data
      const updatedInventory = previous.inventory.map((item) => {
        if (item.id === subscriptionData.data?.onInventoryItemUpdated.id) {
          return {
            ...item,
            ...subscriptionData.data.onInventoryItemUpdated,
          };
        }
        return item;
      });

      cache.writeQuery({
        query: InventoryDocument,
        data: {
          inventory: updatedInventory,
        },
      });
    },
  });

  useOnInventoryItemAddedSubscription({
    variables: {
      accountId,
    },
    onSubscriptionData: ({ client, subscriptionData }) => {
      const cache = client.cache;
      const previous = cache.readQuery<{ inventory: InventoryItem[] }>({
        query: InventoryDocument,
      });

      if (!previous || !subscriptionData.data?.onInventoryItemAdded) {
        return;
      }

      // Map the inventory, and replace the old stock with the new one
      const updatedInventory = [
        ...previous.inventory,
        subscriptionData.data?.onInventoryItemAdded,
      ];

      cache.writeQuery({
        query: InventoryDocument,
        data: {
          inventory: updatedInventory,
        },
      });
    },
  });

  useOnInventoryItemDeletedSubscription({
    variables: {
      accountId,
    },
    onSubscriptionData: ({ client, subscriptionData }) => {
      const cache = client.cache;
      const previous = cache.readQuery<{ inventory: InventoryItem[] }>({
        query: InventoryDocument,
      });

      if (!previous) {
        return;
      }

      const deletedProductId = subscriptionData.data?.onInventoryItemDeleted.id;

      // Filter the inventory, to remove the item from the list
      const updatedInventory = previous.inventory.filter(
        (item) => item.id !== deletedProductId
      );

      cache.writeQuery({
        query: InventoryDocument,
        data: {
          inventory: updatedInventory,
        },
      });
    },
  });

  // Websocket order updates disabled to ensure Receive component uses polling only
  // useOnOrderUpdatedSubscription({
  //   variables: {
  //     accountId,
  //   },
  //   onSubscriptionData: ({ client, subscriptionData }) => {
  //     // Order updates handled by polling in Receive component
  //   },
  // });

  // Websocket order additions disabled to ensure Receive component uses polling only
  // useOnOrderAddedSubscription({
  //   variables: {
  //     accountId,
  //   },
  //   onSubscriptionData: ({ client, subscriptionData }) => {
  //     // Order additions handled by polling in Receive component
  //   },
  // });

  // Websocket order deletions disabled to ensure Receive component uses polling only
  // useOnOrderDeletedSubscription({
  //   variables: {
  //     accountId,
  //   },
  //   onSubscriptionData: ({ client, subscriptionData }) => {
  //     // Order deletions handled by polling in Receive component
  //   },
  // });

  useOnScansUpdatedSubscription({
    variables: {
      accountId,
    },
    onSubscriptionData: ({ client, subscriptionData }) => {
      const cache = client.cache;
      const previous = cache.readQuery<{ scans: Scan }>({
        query: ScansDocument,
      });

      if (!previous) {
        return;
      }

      const newData = subscriptionData.data?.onScansUpdated;

      if (!newData) {
        return;
      }

      cache.writeQuery({
        query: ScansDocument,
        data: {
          scans: {
            ...previous.scans,
            items: newData.items,
          },
        },
      });
    },
  });

  return <>{children}</>;
};
